import type { RevenueSource } from '@tool-factory/registry';
import { useMemo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export interface RevenuePoint {
  day: string;
  total_cents: number;
  by_source: Record<RevenueSource, number>;
}

// Brand palette: gold for primary (lead_sale), charcoal/beige neutrals for secondary
const SOURCE_COLORS: Record<RevenueSource, string> = {
  lead_sale: '#B68B40', // gold — the headline series
  subscription: '#14171B', // charcoal
  affiliate: '#7A6E55', // muted warm bronze
  one_time: '#C7C2B5', // muted beige (border token)
  house_route: '#3A3F47', // softer charcoal
};

const SOURCE_ORDER: RevenueSource[] = [
  'lead_sale',
  'subscription',
  'affiliate',
  'one_time',
  'house_route',
];

export function RevenueChart({ points }: { points: RevenuePoint[] }) {
  const data = useMemo(
    () =>
      points.map((p) => ({
        day: p.day,
        ...Object.fromEntries(Object.entries(p.by_source).map(([k, v]) => [k, v / 100])),
      })),
    [points],
  );
  return (
    <ResponsiveContainer width="100%" height={320}>
      <AreaChart data={data} margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(45 11% 76% / 0.5)" />
        <XAxis
          dataKey="day"
          fontSize={11}
          stroke="hsl(216 14% 9% / 0.55)"
          tick={{ fill: 'hsl(216 14% 9% / 0.65)' }}
        />
        <YAxis
          fontSize={11}
          stroke="hsl(216 14% 9% / 0.55)"
          tick={{ fill: 'hsl(216 14% 9% / 0.65)' }}
          tickFormatter={(v) => `$${v.toLocaleString()}`}
        />
        <Tooltip
          contentStyle={{
            background: 'hsl(0 0% 100%)',
            border: '1px solid hsl(45 11% 76%)',
            borderRadius: '6px',
            fontSize: '12px',
            fontFamily: 'Inter, system-ui, sans-serif',
          }}
          labelStyle={{ color: 'hsl(216 14% 9%)', fontWeight: 600 }}
          formatter={(v: number) => `$${v.toLocaleString()}`}
        />
        <Legend
          wrapperStyle={{
            fontSize: '11px',
            fontFamily: 'Inter, system-ui, sans-serif',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        />
        {SOURCE_ORDER.map((src) => (
          <Area
            key={src}
            type="monotone"
            dataKey={src}
            stackId="rev"
            stroke={SOURCE_COLORS[src]}
            fill={SOURCE_COLORS[src]}
            fillOpacity={src === 'lead_sale' ? 0.35 : 0.55}
            strokeWidth={src === 'lead_sale' ? 2 : 1.2}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}
